<template>
  <div class="qr-analytics-page">
    <qr-analytics-header
      :date-from.sync="dateFrom"
      :date-to.sync="dateTo"
      :company-id.sync="companyId"
      :loading="loading"
      @search="handleSearch"
      @download="handleDownload"
    />

    <div v-if="loading && !hasData" class="qr-analytics-page__loading">
      <p>Loading QR Booker analytics…</p>
    </div>

    <div v-else class="qr-analytics-page__content">
      <div class="qr-analytics-page__kpis">
        <qr-kpi-card
          v-for="kpi in data.kpis"
          :key="kpi.title"
          :title="kpi.title"
          :value="kpi.value"
          :icon="kpi.icon"
          :icon-color="kpi.iconColor"
          :highlight="!!kpi.highlight"
        />
      </div>

      <div class="qr-analytics-page__charts">
        <div class="qr-analytics-page__chart-col">
          <qr-customer-journey
            :stages="data.funnel"
            :revenue="data.revenue"
          />
        </div>
        <div class="qr-analytics-page__chart-col">
          <qr-conversion-rates
            :rates="data.conversionRates"
            :overall-value="data.overallSuccess"
          />
        </div>
      </div>

      <qr-venue-performance
        :venues="data.venues"
        :venue-filter.sync="venueFilter"
      />
    </div>
  </div>
</template>

<script>
import { getQrBookerAnalytics } from "@/services/QRBooker";
import QrAnalyticsHeader from "./components/QrAnalyticsHeader.vue";
import QrKpiCard from "./components/QrKpiCard.vue";
import QrCustomerJourney from "./components/QrCustomerJourney.vue";
import QrConversionRates from "./components/QrConversionRates.vue";
import QrVenuePerformance from "./components/QrVenuePerformance.vue";
import {
  emptyAnalytics,
  getDefaultDateRange,
  normalizeAnalytics,
} from "./analyticsHelpers";

export default {
  name: "QRBookerAnalytics",
  components: {
    QrAnalyticsHeader,
    QrKpiCard,
    QrCustomerJourney,
    QrConversionRates,
    QrVenuePerformance,
  },
  data() {
    const range = getDefaultDateRange();
    return {
      dateFrom: range.from,
      dateTo: range.to,
      companyId: "all",
      venueFilter: "all",
      loading: false,
      hasData: false,
      data: emptyAnalytics,
    };
  },
  mounted() {
    this.fetchAnalytics();
  },
  methods: {
    formatDate(date) {
      if (!date) return "";
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    handleSearch() {
      this.fetchAnalytics();
    },
    fetchAnalytics() {
      if (this.loading) return;
      this.loading = true;

      const payload = {
        startDate: this.formatDate(this.dateFrom),
        endDate: this.formatDate(this.dateTo),
      };

      if (this.companyId && this.companyId !== "all") {
        payload.companyId = this.companyId;
      }

      getQrBookerAnalytics(payload)
        .then((res) => {
          this.data = normalizeAnalytics(res.data || {});
          this.venueFilter = "all";
          this.hasData = true;
        })
        .catch((error) => {
          const errorMessage =
            (error &&
              error.response &&
              error.response.data &&
              (error.response.data.message ||
                error.response.data.responseMessage)) ||
            (error && error.message) ||
            "Failed to fetch QR Booker analytics";
          this.$vs.notify({
            title: "Error",
            text: errorMessage,
            color: "danger",
            iconPack: "feather",
            icon: "icon-alert-circle",
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    handleDownload() {
      const rows = [
        ["Venue", "QR Opens", "Bookings Made", "Completed", "Conversion"],
        ...this.data.venues.map((v) => [
          v.name,
          v.qrOpens,
          v.bookingsMade,
          v.completed,
          `${v.conversion}%`,
        ]),
      ];
      const csv = rows.map((r) => r.join(",")).join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `qrbooker-analytics-${this.formatDate(
        this.dateFrom
      )}-${this.formatDate(this.dateTo)}.csv`;
      link.click();
      URL.revokeObjectURL(url);

      this.$vs.notify({
        title: "Download",
        text: "Venue performance CSV exported",
        color: "success",
        iconPack: "feather",
        icon: "icon-download",
      });
    },
  },
};
</script>

<style scoped lang="scss">
.qr-analytics-page {
  padding: 0.25rem 0 1.5rem;

  &__loading {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 240px;
    color: #9ca3af;
    font-size: 0.95rem;
  }

  &__kpis {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 1rem;
    margin-bottom: 1.5rem;

    @media (max-width: 1200px) {
      grid-template-columns: repeat(3, 1fr);
    }

    @media (max-width: 768px) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 480px) {
      grid-template-columns: 1fr;
    }
  }

  &__charts {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.5rem;
    margin-bottom: 1.5rem;

    @media (max-width: 992px) {
      grid-template-columns: 1fr;
    }
  }

  &__chart-col {
    min-width: 0;
  }
}
</style>
